import React, {useEffect, useState} from 'react';
import {Box, Tabs, Tab, Divider, Typography} from '@mui/material';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import {TabPanel} from './TabPanel';
import EditPanelDrawerHeader from './edit-panel-header/EditPanelDrawerHeader';
import './EditPanelContainer.scss';
import {KeyValue} from '../../../../common/arr-transfer-list/ArrTransferList';
import {t} from 'i18next';
import {
  DocumentViewerSortRequest,
  ManualSortableItem,
  SortableItem,
} from '../../../../../api/types/document.type';
import {SourceTypeCode} from '../../../../../shared/constants';
import {
  useDocumentViewerManualSort,
  useDocumentViewerOptimizedSort,
  useDocumentViewerSort,
  useDocumentViewerSortConfig,
} from '../../../../../hooks/document/useDocument';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import {
  setActiveDocument,
  setDocumentViewLoaded,
} from '../../../docViewerUtils';
import {setShowCompletedDocs} from '../../../../../redux/slice/docViewSlice';

interface FieldDataTypeItem {
  fieldId: string;
  fieldName: string;
  fieldLabel: string;
  fieldDataType: string;
}

interface SortConfiguration {
  optimizedCols: string;
  optimizedSelectFieldIDs: string;
  optimizedSelectFieldOrder: string;
  manualCols: string;
  manualSelectFieldIDs: string;
  manualSelectFieldOrder: string;
  selectFields: string;
  configuredCols: string;
  order: string;
  showCompletedDocOptimized: boolean;
  showCompletedDocManual: boolean;
  configuredShowCompletedDoc: boolean;
  selectFieldTemp: string;
  selectFieldDataTypeOptimized: FieldDataTypeItem[];
  selectFieldDataType: FieldDataTypeItem[];
  selectFieldDataTypeManual: FieldDataTypeItem[];
  redirectionType: number;
  manualOrder: string;
}

interface AssignmentSortDetail {
  sourceID: number;
  columnManual: string;
  columnOptimized: string;
  columnIDsOptimized: string;
  columnOrderOptimized: string;
  columnIDsManual: string;
  columnOrderManual: string;
  showCompletedOptimized: boolean;
  showCompletedManual: boolean;
  columnDisplayManual: string;
  columnDataTypeOptimized: FieldDataTypeItem[];
  columnDataTypeManual: FieldDataTypeItem[];
}

interface DocViewSessionRequest {
  sourceTypeCode: string;
  sourceID: number;
  sortconfiguration: SortConfiguration;
  assignmentSortDetails: AssignmentSortDetail[];
}

interface EditPanelPayload {
  selectField: string;
  selectFieldNames: string;
  order: string;
  selectFieldDisplay: string;
  sortType: number;
  selectFieldDataType: FieldDataTypeItem[];
  docViewSessionRequest: DocViewSessionRequest;
  isShowCompletedDocument?: boolean;
}

interface EditPanelProps {
  onDrawerClose: () => void;
  onSaveConfirm?: () => void;
  documentSortingContent?: React.ReactNode;
}
export enum EditPanelSortType {
  optimizedSortOrder = 'OPTIMIZED',
  manualSortOrder = 'MANUAL',
}

const EditPanelContainer: React.FC<EditPanelProps> = ({
  onDrawerClose,
  onSaveConfirm,
}) => {
  const dispatch = useDispatch();
  const [sortType, setSortType] = useState<string>('');
  const [initSortType, setInitSortType] = useState<string>('');
  const [headerActionDisable, SetHeaderActionDisable] =
    useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [checkedColumn, setCheckedColumn] = useState<KeyValue[]>([]);
  const [availableColumnList, setAvailableColumnList] = useState<KeyValue[]>(
    []
  );
  const [selectedColumnList, setSelectedColumnList] = useState<KeyValue[]>([]);

  const [availableSortingColumnOrder, setAvailableSortingColumnOrder] =
    useState<KeyValue[]>([]);
  const [sortingColumnOrder, setSortingColumnOrder] = useState<KeyValue[]>([]);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const {mutate: documentViewerSort} = useDocumentViewerSort((data) => {
    setSortType(data.redirectionMode);
    setInitSortType(data.redirectionMode);
    handleSortAPIReuest(data.redirectionMode);
  });

  const {mutate: optimizedSort} = useDocumentViewerOptimizedSort((data) => {
    const toKeyValue = (item: SortableItem): KeyValue => ({
      id: Number(item.id),
      label: item.text ?? item.name ?? String(item.id),
      name: item.name,
      dataType: item.datatype,
      disabled: data.sortableList2.includes(item),
    });
    setAvailableColumnList([
      ...data.sortableList2.map(toKeyValue),
      ...data.sortableList1.map(toKeyValue),
    ]);
    setSelectedColumnList(data.sortableList2.map(toKeyValue));
    setCheckedColumn(data.sortableList2.map(toKeyValue));
    SetHeaderActionDisable((data.sortableList2 ?? []).length === 4);

    setTabValue(0);
  });
  const {mutate: saveSortConfig} = useDocumentViewerSortConfig(() => {
    setDocumentViewLoaded(false);
    setActiveDocument(null);
    onDrawerClose();
    onSaveConfirm?.();
  });
  const {mutate: manualSort} = useDocumentViewerManualSort((data) => {
    const toKeyValue = (item: ManualSortableItem): KeyValue => ({
      id: Number(item.id),
      label: item.text ?? item.name ?? String(item.id),
      name: item.name,
      dataType: item.dataType,
      disabled: data.sortList2.includes(item),
    });
    setAvailableColumnList([
      ...data.sortList2.map(toKeyValue),
      ...data.sortList.map(toKeyValue),
    ]);
    setSelectedColumnList(data.sortList2.map(toKeyValue));
    setAvailableSortingColumnOrder([
      ...data.sortList4.map(toKeyValue),
      ...(data.sortList3 ?? []).map(toKeyValue),
    ]);
    const redirectionType =
      docViewConfiguration.sortconfiguration?.redirectionType ?? 0;
    if (redirectionType == 2) {
      setSortingColumnOrder((data.sortList2 ?? []).map(toKeyValue));
    } else {
      setSortingColumnOrder((data.sortList4 ?? []).map(toKeyValue));
    }
    setCheckedColumn(data.sortList2.map(toKeyValue));
    SetHeaderActionDisable((data.sortList2 ?? []).length === 4);
  });

  const docViewConfiguration = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );
  const sourceTypeCode = useSelector(
    (state: RootState) => state.docView.sourceTypeCode
  );
  const documentListMasterId = useSelector(
    (state: RootState) => state.docView.documentListMasterId
  );

  useEffect(() => {
    const payload: DocumentViewerSortRequest = {
      redirectionMode:
        docViewConfiguration.redirectionMode ??
        EditPanelSortType.optimizedSortOrder,
      docViewSessionObj: {
        sourceTypeCode: sourceTypeCode ?? SourceTypeCode.DOCUMENT,
        redirectionType:
          docViewConfiguration.sortconfiguration?.redirectionType ?? 0,
      },
      redirectionType:
        docViewConfiguration.sortconfiguration?.redirectionType ?? 0,
    };
    documentViewerSort(payload);
  }, []);

  const sortOptions = [
    {
      label: t('docView.editpanel.optimizedSortOrder'),
      value: EditPanelSortType.optimizedSortOrder,
    },
    {
      label: t('docView.editpanel.manualSortOrder'),
      value: EditPanelSortType.manualSortOrder,
    },
  ].filter((option) =>
    docViewConfiguration.isAllowManualSort
      ? option
      : option.value !== EditPanelSortType.manualSortOrder
  );

  const buildManualSortParams = () => {
    return {
      manualCols:
        docViewConfiguration.sortconfiguration?.configuredCols
          ?.split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0) ?? [],

      manualOrder:
        docViewConfiguration.sortconfiguration?.redirectionType == 2
          ? docViewConfiguration.sortconfiguration?.selectFieldDataType.map(
              (_) => _.fieldId
            )
          : (docViewConfiguration.sortconfiguration?.order
              ?.split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0) ?? []),
      configuredShowCompletedDoc:
        docViewConfiguration.sortconfiguration?.configuredShowCompletedDoc ??
        false,
      sourceTypeCode: sourceTypeCode ?? SourceTypeCode.DOCUMENT,
    };
  };

  const buildOptimizedSortPayload = (filedSortType: string) => {
    let redirectionType =
      docViewConfiguration.sortconfiguration?.redirectionType ?? 0;
    if (EditPanelSortType.optimizedSortOrder === filedSortType) {
      redirectionType = 2;
    } else if (EditPanelSortType.manualSortOrder === filedSortType) {
      redirectionType = 1;
    }
    return {
      docViewSessionObject: {
        sortconfiguration: {
          optimizedCols:
            docViewConfiguration.sortconfiguration?.optimizedCols ?? '',
          optimizedSelectFieldIDs:
            docViewConfiguration.sortconfiguration?.optimizedSelectFieldIDs ??
            '',
          optimizedSelectFieldOrder:
            docViewConfiguration.sortconfiguration?.optimizedSelectFieldOrder ??
            '',
          manualCols: docViewConfiguration.sortconfiguration?.manualCols ?? '',
          manualSelectFieldIDs:
            docViewConfiguration.sortconfiguration?.manualSelectFieldIDs ?? '',
          manualSelectFieldOrder:
            docViewConfiguration.sortconfiguration?.manualSelectFieldOrder ??
            '',
          selectFields:
            docViewConfiguration.sortconfiguration?.selectFields ?? '',
          configuredCols:
            docViewConfiguration.sortconfiguration?.configuredCols ?? '',
          order: docViewConfiguration.sortconfiguration?.order ?? '',
          showCompletedDocOptimized:
            docViewConfiguration.sortconfiguration?.showCompletedDocOptimized ??
            false,
          showCompletedDocManual:
            docViewConfiguration.sortconfiguration?.showCompletedDocManual ??
            false,
          configuredShowCompletedDoc:
            docViewConfiguration.sortconfiguration
              ?.configuredShowCompletedDoc ?? false,
          selectFieldTemp:
            docViewConfiguration.sortconfiguration?.selectFieldTemp ?? '',
          selectFieldDataTypeOptimized:
            docViewConfiguration.sortconfiguration
              ?.selectFieldDataTypeOptimized ?? [],
          selectFieldDataType:
            docViewConfiguration.sortconfiguration?.selectFieldDataType ?? [],
          selectFieldDataTypeManual:
            docViewConfiguration.sortconfiguration?.selectFieldDataTypeManual ??
            [],
          redirectionType,
          manualOrder:
            docViewConfiguration.sortconfiguration?.manualOrder ?? '',
        },
        assignmentSortDetails: [
          ...(docViewConfiguration.assignmentSortDetails ?? []),
        ],

        documentListMasterID: Number(documentListMasterId),
        sourceTypeCode: sourceTypeCode ?? SourceTypeCode.DOCUMENT,
        assignmentSource: '',
        xmlCompletedDocumentEditedCodingForm: '',
        docsForCodeSame: [],
        docIdsForDocHistory: [],
        backtoSrcSourceTypeCode: '',
      },
    };
  };

  const handleSortAPIReuest = (value: string) => {
    if (value === EditPanelSortType.optimizedSortOrder) {
      optimizedSort(buildOptimizedSortPayload(value));
    } else if (value === EditPanelSortType.manualSortOrder) {
      manualSort(buildManualSortParams());
    }
  };

  const handleSortTypeChange = (value: string) => {
    setSortType(value);
    if (value === EditPanelSortType.optimizedSortOrder) setTabValue(0);
    handleSortAPIReuest(value);

    setSaveDisabled(
      checkedColumn.length === 0 || initSortType === value ? true : false
    );
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const selectedIds = new Set(
      newValue == 0
        ? selectedColumnList.map((_) => _.id)
        : sortingColumnOrder.map((_) => _.id)
    );

    setCheckedColumn(newValue == 0 ? selectedColumnList : sortingColumnOrder);
    if (newValue == 0) {
      setAvailableColumnList((prev) =>
        prev.map((item) =>
          selectedIds.has(item.id)
            ? {...item, disabled: true}
            : {...item, disabled: false}
        )
      );
    } else {
      setAvailableSortingColumnOrder((prev) =>
        prev.map((item) =>
          selectedIds.has(item.id)
            ? {...item, disabled: true}
            : {...item, disabled: false}
        )
      );
    }
    SetHeaderActionDisable(selectedIds.size == 4);
    setTabValue(newValue);
  };

  const handleAssignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: KeyValue
  ) => {
    const isChecked = event.target.checked;

    setCheckedColumn((prev) => {
      if (isChecked) {
        if (prev.length >= 4) return prev;
        const alreadyExists = prev.some((_) => _.id === item.id);
        return alreadyExists ? prev : [...prev, item];
      } else {
        return prev.filter((_) => _.id !== item.id);
      }
    });
  };

  const handleHeaderAction = () => {
    if (checkedColumn.length > 4) return;
    const selectedIds = new Set(checkedColumn.map((_) => _.id));
    SetHeaderActionDisable(checkedColumn.length == 4);
    if (tabValue == 0) {
      setAvailableColumnList((prev) =>
        prev.map((item) =>
          selectedIds.has(item.id) ? {...item, disabled: true} : item
        )
      );
      setSelectedColumnList(checkedColumn);
      setSaveDisabled(checkedColumn.length === 0 ? true : false);
    } else {
      setSaveDisabled(checkedColumn.length === 0 ? true : false);

      setAvailableSortingColumnOrder((prev) =>
        prev.map((item) =>
          selectedIds.has(item.id) ? {...item, disabled: true} : item
        )
      );
      setSortingColumnOrder(checkedColumn);
    }
  };

  const handleReorderSelected = (newOrder: KeyValue[]) => {
    if (tabValue == 0) {
      setSelectedColumnList(newOrder);
    } else {
      setSortingColumnOrder(newOrder);
    }
    setSaveDisabled(checkedColumn.length === 0 ? true : false);
  };

  const handleRemoveSelected = (field: KeyValue) => {
    setCheckedColumn((prev) => prev.filter((_) => _.id !== field.id));

    if (tabValue == 0) {
      setAvailableColumnList((prev) =>
        prev.map((item) =>
          item.id === field.id ? {...item, disabled: false} : item
        )
      );
      setSaveDisabled(selectedColumnList.length === 1 ? true : false);

      setSelectedColumnList((prev) => prev.filter((_) => _.id !== field.id));
    } else {
      setSaveDisabled(checkedColumn.length === 1 ? true : false);
      setAvailableSortingColumnOrder((prev) =>
        prev.map((item) =>
          item.id === field.id ? {...item, disabled: false} : item
        )
      );
      setSortingColumnOrder((prev) => prev.filter((_) => _.id !== field.id));
    }

    SetHeaderActionDisable(false);
  };

  const handleOnSaveClick = () => {
    const redirectionType =
      EditPanelSortType.optimizedSortOrder === sortType ? 2 : 1;
    const payload: EditPanelPayload = {
      isShowCompletedDocument:
        sourceTypeCode === SourceTypeCode.ASSIGNMENT
          ? showCompletedDocument
          : undefined,
      selectField:
        selectedColumnList.map((field) => `MD:${field.id}:A`).join(',') + ',',
      selectFieldNames: selectedColumnList.map((field) => field.name).join(','),
      order:
        EditPanelSortType.optimizedSortOrder === sortType
          ? ''
          : sortingColumnOrder.length > 0
            ? sortingColumnOrder.map((field) => field.id).join(',')
            : selectedColumnList.map((field) => field.id).join(','),
      selectFieldDisplay: selectedColumnList
        .map((field) => field.name)
        .join(','),
      sortType: sortType === EditPanelSortType.optimizedSortOrder ? 2 : 1,
      selectFieldDataType: selectedColumnList.map((field) => ({
        fieldName: field.name ?? '',
        fieldDataType: field.dataType ?? '',
        fieldId: field.id.toString(),
        fieldLabel: field.label ?? '',
      })),
      docViewSessionRequest: {
        sourceTypeCode: sourceTypeCode ?? SourceTypeCode.DOCUMENT,
        sourceID: 0,
        sortconfiguration: {
          optimizedCols:
            docViewConfiguration.sortconfiguration?.optimizedCols ?? '',
          optimizedSelectFieldIDs:
            docViewConfiguration.sortconfiguration?.optimizedSelectFieldIDs ??
            '',
          optimizedSelectFieldOrder:
            docViewConfiguration.sortconfiguration?.optimizedSelectFieldOrder ??
            '',
          manualCols: docViewConfiguration.sortconfiguration?.manualCols ?? '',
          manualSelectFieldIDs:
            docViewConfiguration.sortconfiguration?.manualSelectFieldIDs ?? '',
          manualSelectFieldOrder:
            docViewConfiguration.sortconfiguration?.manualSelectFieldOrder ??
            '',
          selectFields:
            docViewConfiguration.sortconfiguration?.selectFields ?? '',
          configuredCols:
            docViewConfiguration.sortconfiguration?.configuredCols ?? '',
          order: docViewConfiguration.sortconfiguration?.order ?? '',
          showCompletedDocOptimized:
            docViewConfiguration.sortconfiguration?.showCompletedDocOptimized ??
            false,
          showCompletedDocManual:
            docViewConfiguration.sortconfiguration?.showCompletedDocManual ??
            false,
          configuredShowCompletedDoc:
            docViewConfiguration.sortconfiguration
              ?.configuredShowCompletedDoc ?? false,
          selectFieldTemp:
            docViewConfiguration.sortconfiguration?.selectFieldTemp ?? '',
          selectFieldDataTypeOptimized:
            docViewConfiguration.sortconfiguration
              ?.selectFieldDataTypeOptimized ?? [],
          selectFieldDataType:
            docViewConfiguration.sortconfiguration?.selectFieldDataType ?? [],
          selectFieldDataTypeManual:
            docViewConfiguration.sortconfiguration?.selectFieldDataTypeManual ??
            [],
          redirectionType,
          manualOrder:
            docViewConfiguration.sortconfiguration?.manualOrder ?? '',
        },
        assignmentSortDetails: [
          {
            sourceID: 0,
            columnManual: '',
            columnOptimized: '',
            columnIDsOptimized: '',
            columnOrderOptimized: '',
            columnIDsManual: '',
            columnOrderManual: '',
            showCompletedOptimized: true,
            showCompletedManual: true,
            columnDisplayManual: '',
            columnDataTypeOptimized: [],
            columnDataTypeManual: [],
          },
        ],
      },
    };
    saveSortConfig(payload);
  };
  const [showCompletedDocument, setShowCompletedDocument] = useState(
    docViewConfiguration.sortconfiguration?.configuredShowCompletedDoc ?? false
  );

  const handleToggleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setShowCompletedDocument(checked);
    setSaveDisabled(checkedColumn.length === 0 ? true : false);
    dispatch(setShowCompletedDocs(checked));
  };
  return (
    <Box className="edit-panel-container">
      <Box
        className="edit-panel-container-header"
        data-tab-group="miniDocList"
        data-tab-id="edit-panel-container-header"
      >
        <EditPanelDrawerHeader
          onSave={handleOnSaveClick}
          onCancel={onDrawerClose}
          saveDisabled={saveDisabled}
        />
      </Box>
      <Box className="edit-panel-container-body">
        <Box className="toggle-radio-btn-container">
          <Box className="radio-btn-container">
            {sourceTypeCode === SourceTypeCode.ASSIGNMENT && (
              <Box className="toggle-btn-container">
                <ArrToggle
                  disabled={false}
                  checked={showCompletedDocument}
                  toggleSize="medium"
                  onChange={handleToggleChange}
                  dataTestId="source-type-toggle"
                />
                <Typography
                  data-testid={`edit-panel-container-show-completed-doc-list`}
                >
                  {t('docView.editpanel.showCompletedDocList')}
                </Typography>
              </Box>
            )}
            <ArrRadio
              dataTestId="sortOptions"
              data={sortOptions}
              value={sortType}
              onChange={handleSortTypeChange}
              name="sort-type"
              size="small"
              className="radio-btn"
            />
          </Box>
        </Box>
        <Box className="edit-panel-tabs">
          <Box className="tab">
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="drawer tabs"
              variant="standard"
            >
              <Tab
                label={t('docView.editpanel.pickColumnDisplay')}
                aria-controls="drawer-tabpanel-0"
              />
              {sortType === EditPanelSortType.manualSortOrder && (
                <Tab
                  label={t('docView.editpanel.setDocumentSorting')}
                  aria-controls="drawer-tabpanel-1"
                />
              )}
            </Tabs>
          </Box>
          <Divider orientation="horizontal" flexItem className="arr-divider" />
          <Box className="tab-panel">
            <TabPanel
              selectedAssignedList={
                tabValue == 0 ? selectedColumnList : sortingColumnOrder
              }
              availableAssignedList={
                tabValue == 0
                  ? availableColumnList
                  : availableSortingColumnOrder
              }
              handleAssignChange={handleAssignChange}
              onHeaderAction={handleHeaderAction}
              onReorderSelected={handleReorderSelected}
              onRemoveSelected={handleRemoveSelected}
              checkColumnList={checkedColumn}
              headerActionDisable={headerActionDisable}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditPanelContainer;
